/** The SL / Target tiles on the open-position card: what number goes in
 * them, and in which unit.
 *
 * Split out of `OpenPositionCard` (2026-09-16) when the index-level fallback
 * pushed that file past its line gate. Pure functions over one trade — no
 * rendering, so the unit rules below are testable on their own. */

import type { TradeLogEntry } from "@/types/tradeLog";

/** Books at the nearest confirmed pivot swing high above the current
 * premium — recomputed live, so "pending" means no confirmed swing sits
 * above price yet (ride the stop until one forms). */
function targetHint(trade: TradeLogEntry): string {
  if (trade.targetPrice === null) return "no swing above yet";
  if (trade.entryPremium === null) return "books here";
  return `${(trade.targetPrice - trade.entryPremium).toFixed(2)} vs entry`;
}

interface LevelTile {
  label: string;
  value: string;
  hint: string | undefined;
}

/** True when this trade's stop lives on the INDEX, not on the premium.
 *
 * `currentSlPrice` / `targetPrice` are PREMIUM levels fed by a broker stop
 * order; an engine that parks none (POI-1: `places_stop = False`,
 * `app/scheduling/poi1_executor.py`) writes NULL there and commits to index
 * levels on `signal_stop` / `signal_target` instead. The card showed a bare
 * dash for those (operator, 2026-09-16) while the engine watched a stop
 * every tick — POI's is a 5-min index CLOSE beyond the level
 * (`docs/poi1.md` §6). */
function usesIndexLevels(trade: TradeLogEntry): boolean {
  return trade.currentSlPrice === null && trade.signalStop !== null;
}

/** Entry premium, with the INDEX level it was taken at underneath.
 *
 * Operator, 2026-09-29: *"should have nifty price along with option"*. A bare
 * "Rs 145.12" says nothing about where NIFTY was when the trade opened, so the
 * SL and Target index levels beside it had nothing to be read against. The
 * index comes from `signal_entry`, which every engine already stamps — it is
 * NOT derived from the premium. */
export function entryTile(trade: TradeLogEntry): LevelTile {
  return {
    label: "Entry",
    value: formatPrice(trade.entryPremium),
    hint: trade.signalEntry === null ? undefined : `${formatIndex(trade.signalEntry)} index`,
  };
}

export function slTile(trade: TradeLogEntry): LevelTile {
  if (!usesIndexLevels(trade)) {
    return { label: "SL", value: formatPrice(trade.currentSlPrice), hint: slHint(trade) };
  }
  return { label: "SL (index)", value: formatIndex(trade.signalStop), hint: "index level" };
}

export function targetTile(trade: TradeLogEntry): LevelTile {
  if (!usesIndexLevels(trade)) {
    return { label: "Target", value: formatPrice(trade.targetPrice), hint: targetHint(trade) };
  }
  const hint = trade.signalTarget === null ? "no target — runs on the stop" : "index level";
  return { label: "Target (index)", value: formatIndex(trade.signalTarget), hint };
}

export function formatPrice(v: number | null): string {
  if (v === null) return "—";
  return `Rs ${v.toFixed(2)}`;
}

/** An INDEX price — deliberately NOT prefixed "Rs", so a 23,196.15 index
 * stop can never be read as a premium beside a Rs 205.57 entry. */
export function formatIndex(v: number | null): string {
  if (v === null) return "—";
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function slHint(trade: TradeLogEntry): string | undefined {
  if (trade.currentSlPrice === null || trade.entryPremium === null) return undefined;
  const diff = trade.currentSlPrice - trade.entryPremium;
  const sign = diff >= 0 ? "+" : "";
  return `${sign}${diff.toFixed(0)} vs entry`;
}

export function hwmHint(trade: TradeLogEntry): string | undefined {
  if (trade.highWaterMark === null || trade.entryPremium === null) return undefined;
  const diff = trade.highWaterMark - trade.entryPremium;
  const sign = diff >= 0 ? "+" : "";
  return `${sign}${diff.toFixed(0)} above entry`;
}
